import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconMagic } from '@sorare/core/src/atoms/icons/system/iconMagic.ts';
import { iconPen } from '@sorare/core/src/atoms/icons/system/iconPen.ts';
import { iconXCircle } from '@sorare/core/src/atoms/icons/system/iconXCircle.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { DotsLoader } from '@sorare/core/src/atoms/loader/DotsLoader/index.tsx';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { useDeleteTaskLineup } from '../useDeleteTaskLineup';
import type { LineupButtons_lineupInterface } from './__generated__/index.graphql.ts';

type SuggestLineupProps = {
  enabled?: boolean;
  onSuggestLineup?: () => void;
  loading?: boolean;
};
type Props = {
  lineup?: LineupButtons_lineupInterface;
  composeTeamUrl: string;
  onDeleteDone?: () => void;
  suggestLineupProps?: SuggestLineupProps;
};
export const LineupButtons = ({
  lineup,
  composeTeamUrl,
  onDeleteDone,
  suggestLineupProps,
}: Props) => {
  const [deleteTaskLineup] = useDeleteTaskLineup();
  if (!lineup) {
    return (
      <Horizontal>
        {!!suggestLineupProps?.enabled && (
          <LinkOther
            size="medium"
            as={Button}
            disableDebounce={false}
            color="transparent"
            disabled={suggestLineupProps.loading}
            onClick={suggestLineupProps.onSuggestLineup}
            fullWidth
          >
            {suggestLineupProps.loading ? (
              <DotsLoader />
            ) : (
              <Horizontal>
                <FontAwesomeIcon icon={iconMagic} />
                <BodyS as="p" bold>
                  <FormattedMessage {...fantasy.suggestLineup} />
                </BodyS>
              </Horizontal>
            )}
          </LinkOther>
        )}
        <LinkOther
          as={Button}
          color="transparent"
          to={composeTeamUrl}
          fullWidth
          size="medium"
        >
          <Horizontal>
            <FontAwesomeIcon icon={iconPen} />
            <BodyS as="p" bold>
              <FormattedMessage {...fantasy.addTeam} />
            </BodyS>
          </Horizontal>
        </LinkOther>
      </Horizontal>
    );
  }
  const { deletable, updatable } = lineup;
  if (deletable || updatable) {
    return (
      <Horizontal>
        {!!deletable && (
          <LinkOther
            as={Button}
            size="medium"
            color="transparent"
            fullWidth
            onClick={async () => {
              await deleteTaskLineup({
                variables: {
                  input: {
                    taskLineupId: lineup.id,
                  },
                },
              });
              onDeleteDone?.();
            }}
          >
            <Horizontal>
              <FontAwesomeIcon icon={iconXCircle} />
              <BodyS as="p" bold>
                <FormattedMessage defaultMessage="Clear Team" />
              </BodyS>
            </Horizontal>
          </LinkOther>
        )}
        {!!updatable && (
          <LinkOther
            as={Button}
            color="transparent"
            to={composeTeamUrl}
            fullWidth
            size="medium"
          >
            <Horizontal>
              <FontAwesomeIcon icon={iconPen} />
              <BodyS as="p" bold>
                <FormattedMessage {...fantasy.editLineup} />
              </BodyS>
            </Horizontal>
          </LinkOther>
        )}
      </Horizontal>
    );
  }
  return null;
};

LineupButtons.fragments = {
  lineupInterface: gql`
    fragment LineupButtons_lineupInterface on LineupInterface {
      id
      deletable
      updatable
    }
  ` as TypedDocumentNode<LineupButtons_lineupInterface>,
};
